import { z } from 'zod';
import type { SiteRagReport } from '../../types/siteRag';

/** Read-only saved-body input. Older immutable reports may retain a semantic
 * decision without the input envelope required for a NEW authoritative analysis.
 * Keep those original bytes opaque here; this type cannot assert that envelope. */
export type ScreeningSummarySavedReport = Omit<SiteRagReport, 'decisionAnalysis'> & {
  decisionAnalysis?: unknown;
};

// Validate only the recorded fields the Summary actually consumes. Unconsumed
// analysis identities, inputs and other semantic content remain in the saved
// body; they are neither reconstructed nor adopted as current analysis authority.
const summaryDecisionStateSchema = z.object({
  semantic: z.object({
    systemDecision: z.object({
      band: z.enum(['go', 'conditional', 'no-go', 'withheld']),
    }),
    conditions: z.array(z.object({
      status: z.enum(['open', 'satisfied', 'waived']),
      // A legacy reviewer-voice hold can withhold this leaf. Shared text hygiene
      // materializes its recorded notice; null never clears the saved condition.
      requirement: z.string().nullable(),
    })).optional(),
    unknowns: z.array(z.object({ field: z.string() })).optional(),
  }),
});

/** A malformed supplied decision cannot silently fall back to a positive
 * narrative verdict. Parsing is presentation-only and never alters saved bytes. */
export function readSummaryDecisionState(value: unknown) {
  if (value == null) return undefined;
  const parsed = summaryDecisionStateSchema.safeParse(value);
  if (!parsed.success) {
    throw new Error('The saved screening decision has no valid presentation state. Regenerate the report before viewing or exporting it.');
  }
  return parsed.data.semantic;
}
